import { useMemo } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { LATER } from '@rundown/domain';
import { EmptyState, Pill, PageContainer, PageHeader } from '../../components';
import { formatHours } from '../../lib/format';
import { cx } from '../../lib/cx';
import { useRequiredWorkspace } from '../workspace/useWorkspace';
import { useCapability } from '../workspace/useCapability';
import { useVendorName } from '../workspace/useWorkspace';
import { RequestMenu } from '../requests/RequestMenu';
import { backlogQueryOptions, isInternalBacklogRow, projectionQueryOptions, type BacklogRow } from './api';
import { useBacklogRank } from './useBacklogRank';
import { PRIORITY_LABELS, PRIORITY_TONES } from './labels';

/**
 * The Backlog — every request not yet committed to a sprint, in rank order.
 *
 * The Client Owner's own ordering IS the priority signal Noorix plans
 * against (v0.3 §6.1); this screen is where they set it, by dragging. Noorix
 * sees the same list and the same "would land in" projection, so a planning
 * conversation has one shared picture rather than the client's guess and
 * Noorix's spreadsheet disagreeing.
 *
 * A caller without `backlog.rank` (a Requester, or a Noorix Developer who
 * happens not to hold it) sees the identical list with no drag handles —
 * per useCapability's own doc comment, that is a UI convenience: the write
 * itself is refused server-side regardless of what this component renders.
 */

const PROJECTION_CAPACITY_MODE = 'team' as const;
const PROJECTION_FILL = 'strict-rank' as const;

function landingLabel(
  sprintKey: string | undefined,
  sprintNameByKey: Map<string, string>,
  unestimated = false,
): string {
  if (sprintKey === undefined) return '';
  // Not "Later": nobody knows how big it is, so nobody knows when it lands.
  if (unestimated) return 'Needs analysis';
  if (sprintKey === LATER) return 'Later';
  return sprintNameByKey.get(sprintKey) ?? sprintKey;
}

interface RowProps {
  readonly row: BacklogRow;
  readonly landing: string;
  readonly draggable: boolean;
  readonly workspaceId: string;
  /** `board.write`: whether this row offers the status menu at all. */
  readonly canMove: boolean;
}

function BacklogRowView({ row, landing, draggable, workspaceId, canMove }: RowProps) {
  const vendorName = useVendorName();
  const sortable = useSortable({ id: row.id, disabled: !draggable });
  const internal = isInternalBacklogRow(row) ? row : null;
  const priorityTone = PRIORITY_TONES[row.priority];

  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  };

  return (
    <div
      ref={sortable.setNodeRef}
      style={style}
      className={cx(
        'flex items-center gap-3 rounded-xl border border-line bg-base px-3.5 py-3',
        sortable.isDragging && 'z-10 shadow-lg',
      )}
    >
      {draggable && (
        <button
          type="button"
          aria-label={`Reorder #${row.number}`}
          className="flex-none cursor-grab touch-none rounded px-1 py-2 text-muted hover:text-ink active:cursor-grabbing"
          {...sortable.attributes}
          {...sortable.listeners}
        >
          ⠿
        </button>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="fs-11 whitespace-nowrap text-muted">#{row.number}</span>
          {priorityTone !== null && <Pill tone={priorityTone}>{PRIORITY_LABELS[row.priority]}</Pill>}
          {row.ownSprint && (
            <Pill tone="violet" title="Needs a sprint to itself">
              Own sprint
            </Pill>
          )}
          {internal !== null && !internal.visibleToClient && <Pill tone="violet">Internal</Pill>}
        </div>
        {/* The title opens the request.
            It was the only list in the app where it did not — the Plan cards
            and the Delivery cards have linked titles already — so the backlog
            was a list of things you could reorder and not read. Dragging is on
            the handle to the left rather than the row, so there is nothing for
            this link to compete with. */}
        <Link
          to={`/requests/${row.id}`}
          draggable={false}
          className="mt-0.5 block truncate fs-13.5 font-semibold text-ink hover:text-accent hover:underline"
        >
          {row.title}
        </Link>
        <div className="mt-0.5 truncate fs-11 text-muted">
          {row.projectName ?? 'No project'} ·{' '}
          {row.approxHours > 0 ? formatHours(row.approxHours) : 'Needs analysis'}
          {internal !== null && ` · ${row.ownerName ?? 'Unassigned'}`}
        </div>
      </div>

      {landing !== '' && (
        <Pill
          tone={landing === 'Later' || landing === 'Needs analysis' ? 'neutral' : 'accent'}
          className="flex-none"
          title={
            row.placedSprintId !== null
              ? `Placed here by the client; ${vendorName} confirms at planning`
              : undefined
          }
        >
          {landing}
          {row.placedSprintId !== null && ' · placed'}
        </Pill>
      )}

      {canMove && internal !== null && (
        <RequestMenu
          workspaceId={workspaceId}
          requestId={row.id}
          number={row.number}
          column={internal.boardColumn}
        />
      )}
    </div>
  );
}

export function BacklogPage() {
  const { workspace } = useRequiredWorkspace();
  const canRank = useCapability('backlog.rank');
  const canMove = useCapability('board.write');
  const vendorName = useVendorName();

  const backlogQuery = useQuery(backlogQueryOptions(workspace.id));
  const projectionQuery = useQuery(
    projectionQueryOptions(workspace.id, PROJECTION_CAPACITY_MODE, PROJECTION_FILL),
  );
  const rankMutation = useBacklogRank(workspace.id, PROJECTION_CAPACITY_MODE, PROJECTION_FILL);

  const items = backlogQuery.data ?? [];
  const sprintNameByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const sprint of projectionQuery.data?.sprints ?? []) map.set(sprint.key, sprint.name);
    return map;
  }, [projectionQuery.data]);

  const sensors = useSensors(
    // A small activation distance, which is what tells a click from a drag:
    // the row's title is a link and its menu is a button, and neither may be
    // swallowed by the sortable wrapped around them.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over === null || active.id === over.id) return;

    const oldIndex = items.findIndex((row) => row.id === active.id);
    const newIndex = items.findIndex((row) => row.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    // What the list would look like after the move — used only to read off
    // the two neighbours the moved row now sits between. The actual reorder
    // happens inside useBacklogRank's applyOptimistically, driven by those
    // neighbour ids, not by this array.
    const reordered = arrayMove(items, oldIndex, newIndex);
    const droppedAt = reordered.findIndex((row) => row.id === active.id);

    rankMutation.mutate({
      movedId: String(active.id),
      // Highest-rank-first list: the row above the drop point bounds the new
      // rank from above (afterId), the row below bounds it from below
      // (beforeId) — see reorderByRank's contract in lib/optimistic.ts.
      afterId: reordered[droppedAt - 1]?.id,
      beforeId: reordered[droppedAt + 1]?.id,
    });
  };

  return (
    <PageContainer>
      <PageHeader
        title={'Backlog'}
        description={
          <>
            {canRank
              ? `Drag to set what matters most. This order is what ${vendorName} plans sprints against.`
              : 'What has not yet been committed to a sprint, in priority order.'}
          </>
        }
      />

      {backlogQuery.isPending ? (
        <p className="fs-12 text-muted">Loading…</p>
      ) : backlogQuery.error !== null ? (
        <EmptyState title="Could not load the backlog" description={backlogQuery.error.message} />
      ) : items.length === 0 ? (
        <EmptyState
          title="Nothing waiting"
          description="Every request is either committed to a sprint or has not been posted yet."
        />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((row) => row.id)} strategy={verticalListSortingStrategy}>
            <div className="flex flex-col gap-2">
              {items.map((row) => (
                <BacklogRowView
                  key={row.id}
                  row={row}
                  landing={landingLabel(
                    projectionQuery.data?.landing[row.id],
                    sprintNameByKey,
                    projectionQuery.data?.landings.some(
                      (landing) => landing.itemId === row.id && landing.reason === 'unestimated',
                    ),
                  )}
                  draggable={canRank}
                  workspaceId={workspace.id}
                  canMove={canMove}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {rankMutation.error !== null && (
        <p role="alert" className="mt-3 fs-11 text-danger">
          {rankMutation.error.message}
        </p>
      )}
    </PageContainer>
  );
}
